import ButtonComponent from 'components/ButtonComponent';
import DatePicker from 'components/DatePicker/DatePicker';
import { isStringAValidDate, getFormatedDate } from 'lib/util';
import React, { useEffect, useRef, useState } from 'react';
import { Col, Form } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import './FilterView.scss';
import useDebounce from 'lib/hooks/useDebounce';
import { OrdersState } from '../../containers/Orders/Orders';
import ClearableFormControl from '../ClearableFormControl/ClearableFormControl';
import { useSelector } from 'react-redux';
import { AppState } from 'reducers';
import { useDevice } from 'lib/hooks/useDevice';

interface FilterViewProps {
    handleSearchSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
    orderNumber: string;
    setOrderNumber: React.Dispatch<React.SetStateAction<string>>;
    customerOrderNumber: string;
    setCustomerOrderNumber: React.Dispatch<React.SetStateAction<string>>;
    from: string;
    setFrom: React.Dispatch<React.SetStateAction<string>>;
    to: string;
    setTo: React.Dispatch<React.SetStateAction<string>>;
    description: string;
    setDescription: React.Dispatch<React.SetStateAction<string>>;
    ordersState: OrdersState;
}

const FilterView = (props: FilterViewProps) => {
    const { isDesktop } = useDevice();
    const history = useHistory();

    const [validated, setValidated] = useState(false);

    const orderNumberRef = useRef<HTMLInputElement>(null);
    const customerOrderNumberRef = useRef<HTMLInputElement>(null);
    const descriptionRef = useRef<HTMLInputElement>(null);

    const isFetching = useSelector((state: AppState) => state.data.orders.props.isFetchingOrders);

    const checkValidity = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const form = event.currentTarget;
        if (form.checkValidity() === false) {
            setValidated(true);
            return;
        }
        props.handleSearchSubmit(event);
    };

    const debouncedOrderNumber = useDebounce(props.orderNumber);
    const debouncedCustomerOrderNumber = useDebounce(props.customerOrderNumber);
    const debouncedDescription = useDebounce(props.description);
    const debouncedFrom = useDebounce(props.from);
    const debouncedTo = useDebounce(props.to);

    const handleDebounce = () => {
        history.push(history.location.pathname, {
            ...props.ordersState,
            orderNumber: props.orderNumber,
            customerOrderNumber: props.customerOrderNumber,
            description: props.description,
            from: props.from,
            to: props.to
        });
    };

    useEffect(() => {
        handleDebounce();
    }, [debouncedOrderNumber, debouncedCustomerOrderNumber, debouncedDescription]);

    useEffect(() => {
        if (isStringAValidDate(debouncedFrom)) {
            handleDebounce();
        }
    }, [debouncedFrom]);

    useEffect(() => {
        if (isStringAValidDate(debouncedTo)) {
            handleDebounce();
        }
    }, [debouncedTo]);

    useEffect(() => {
        const activeElem = (document.activeElement as HTMLElement);
        if (activeElem !== orderNumberRef.current && activeElem !== customerOrderNumberRef.current && activeElem !== descriptionRef.current) {
            activeElem.blur();
        }
    }, [history.location.state]);

    return (
        <>
            <Form
                onSubmit={checkValidity}
                className={isDesktop ? 'mb-2' : 'search-form-mobile'}
                style={{ minHeight: 112 }}
                noValidate
                validated={validated}
            >
                <div>
                    {isDesktop ? (
                        <Form.Row>
                            <Form.Group as={Col}>
                                <Form.Label>NUMER</Form.Label>
                                <ClearableFormControl
                                    ref={orderNumberRef}
                                    type='text'
                                    placeholder='Numer zamówienia'
                                    id='orderNumber'
                                    className={props.orderNumber ? 'higlight-when-active' : ''}
                                    value={props.orderNumber}
                                    onChange={(e) => props.setOrderNumber(e.target.value)}
                                    isLoading={isFetching}
                                />
                            </Form.Group>
                            <Form.Group as={Form.Row} style={{ width: '72%' }}>
                                <Form.Group as={Col}>
                                    <Form.Label>NUMER KLIENTA</Form.Label>
                                    <ClearableFormControl
                                        ref={customerOrderNumberRef}
                                        type='text'
                                        placeholder='Numer klienta'
                                        id='customerOrderNumber'
                                        className={props.customerOrderNumber ? 'higlight-when-active' : ''}
                                        value={props.customerOrderNumber}
                                        onChange={(e) => props.setCustomerOrderNumber(e.target.value)}
                                        isLoading={isFetching}
                                    />
                                </Form.Group>
                                <Form.Group as={Col}>
                                    <Form.Label>DATA OD</Form.Label>
                                    <DatePicker
                                        id='from'
                                        type='date'
                                        onChange={(e) => { if (e !== null) props.setFrom(getFormatedDate(e)); }}
                                        className='higlight-when-active'
                                        value={new Date(props.from)}
                                        onBlur={(e) => { if (!isStringAValidDate(e.target.value)) { props.setFrom(props.ordersState.from); } }}
                                        isLoading={isFetching}
                                    />
                                </Form.Group>
                                <Form.Group as={Col}>
                                    <Form.Label>DATA DO</Form.Label>
                                    <DatePicker
                                        id='to'
                                        type='date'
                                        onChange={(e) => { if (e !== null) props.setTo(getFormatedDate(e)); }}
                                        className='higlight-when-active'
                                        value={new Date(props.to)}
                                        onBlur={(e) => { if (!isStringAValidDate(e.target.value)) { props.setTo(props.ordersState.to); } }}
                                        isLoading={isFetching}
                                    />
                                </Form.Group>
                                <Form.Group as={Col}>
                                    <Form.Label>OPIS</Form.Label>
                                    <ClearableFormControl
                                        ref={descriptionRef}
                                        type='text'
                                        placeholder='Uwagi/Opis'
                                        id='description'
                                        className={props.description ? 'higlight-when-active' : ''}
                                        value={props.description}
                                        onChange={(e) => props.setDescription(e.target.value)}
                                        isLoading={isFetching}
                                    />
                                </Form.Group>
                            </Form.Group>
                            <Form.Group as={Form.Row}>
                                <Form.Group as={Col} xs='auto' className='search-button'>
                                    <ButtonComponent
                                        type='submit'
                                        margin='0'
                                        text='Odśwież'
                                        onHoverAnimation={3}
                                        height='38px'
                                    />
                                </Form.Group>
                            </Form.Group>
                        </Form.Row>
                    ) : (
                        <>
                            <Form.Row>
                                <Form.Group className='flex-end' as={Col}>
                                    <Form.Label>NUMER ZAMÓWIENIA</Form.Label>
                                    <ClearableFormControl
                                        type='text'
                                        placeholder='Numer zamówienia'
                                        id='orderNumber'
                                        className={props.orderNumber ? 'higlight-when-active' : ''}
                                        value={props.orderNumber}
                                        onChange={(e) => props.setOrderNumber(e.target.value)}
                                        isLoading={isFetching}
                                    />
                                </Form.Group>
                                <Form.Group className='flex-end' as={Col}>
                                    <Form.Label>NUMER KLIENTA</Form.Label>
                                    <ClearableFormControl
                                        type='text'
                                        placeholder='Numer klienta'
                                        id='customerOrderNumber'
                                        className={props.customerOrderNumber ? 'higlight-when-active' : ''}
                                        value={props.customerOrderNumber}
                                        onChange={(e) => props.setCustomerOrderNumber(e.target.value)}
                                        isLoading={isFetching}
                                    />
                                </Form.Group>
                            </Form.Row>
                            <div>
                                <Form.Row>
                                    <Form.Group className='flex-end' as={Col} controlId='from'>
                                        <Form.Label>DATA OD</Form.Label>
                                        <DatePicker
                                            id='from'
                                            type='date'
                                            onChange={(e) => { if (e !== null) props.setFrom(getFormatedDate(e)); }}
                                            className='higlight-when-active'
                                            value={new Date(props.from)}
                                            onBlur={(e) => { if (!isStringAValidDate(e.target.value)) { props.setFrom(props.ordersState.from); } }}
                                            isLoading={isFetching}
                                        />
                                    </Form.Group>
                                    <Form.Group className='flex-end' as={Col} controlId='to'>
                                        <Form.Label>DATA DO</Form.Label>
                                        <DatePicker
                                            id='to'
                                            type='date'
                                            onChange={(e) => { if (e !== null) props.setTo(getFormatedDate(e)); }}
                                            className='higlight-when-active'
                                            value={new Date(props.to)}
                                            onBlur={(e) => { if (!isStringAValidDate(e.target.value)) { props.setTo(props.ordersState.to); } }}
                                            isLoading={isFetching}
                                        />
                                    </Form.Group>
                                </Form.Row>
                                <Form.Row>
                                    <Form.Group className='flex-end' as={Col}>
                                        <Form.Label>OPIS</Form.Label>
                                        <ClearableFormControl
                                            type='text'
                                            id='description'
                                            placeholder='Uwagi/Opis'
                                            className={props.description ? 'higlight-when-active' : ''}
                                            value={props.description}
                                            onChange={(e) => props.setDescription(e.target.value)}
                                            isLoading={isFetching}
                                        />
                                    </Form.Group>
                                </Form.Row>
                            </div>
                            <Form.Group as={Col} className='search-button'>
                                <ButtonComponent
                                    type='submit'
                                    margin='0'
                                    text='Odśwież'
                                    width='100%'
                                    onHoverAnimation={3}
                                />
                            </Form.Group>
                        </>
                    )}
                </div>

            </Form>
        </>
    );
};

export default FilterView;
